import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const isAuthenticated = authService.isAuthenticated();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showFullComboModal, setShowFullComboModal] = useState(false);

  const tools = [
    {
      id: 1,
      name: t('home.tools.veo3.name'),
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      description: t('home.tools.veo3.desc'),
      features: t('home.tools.veo3.features', { returnObjects: true }) as string[]
    },
    {
      id: 2,
      name: t('home.tools.whisk.name'),
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      description: t('home.tools.whisk.desc'),
      features: t('home.tools.whisk.features', { returnObjects: true }) as string[]
    },
    {
      id: 3,
      name: t('home.tools.combo.name'),
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
      ),
      description: t('home.tools.combo.desc'),
      features: t('home.tools.combo.features', { returnObjects: true }) as string[]
    },
    {
      id: 4,
      name: t('home.tools.fullCombo.name'),
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
      description: t('home.tools.fullCombo.desc'),
      features: t('home.tools.fullCombo.features', { returnObjects: true }) as string[]
    },
    {
      id: 5,
      name: t('home.tools.voice.name'),
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      ),
      description: t('home.tools.voice.desc'),
      features: t('home.tools.voice.features', { returnObjects: true }) as string[]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Zalo Floating Button */}
      <a
        href="https://zalo.me/g/zcfqah968"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group"
        title="Tham gia nhóm Zalo"
      >
        <div className="relative">
          {/* Pulse animation ring */}
          <div className="absolute inset-0 bg-[#0068FF] rounded-full animate-ping opacity-20"></div>

          {/* Main button */}
          <div className="relative bg-[#0068FF] hover:bg-[#0052CC] rounded-full p-2.5 sm:p-3 shadow-2xl hover:shadow-[#0068FF]/60 transition-all duration-300 hover:scale-110 flex items-center justify-center group-hover:ring-4 group-hover:ring-[#0068FF]/40">
            {/* Zalo Official Icon */}
            <svg className="w-14 h-14 sm:w-16 sm:h-16" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Blue circle background */}
              <circle cx="28" cy="28" r="28" fill="#0068FF" />

              {/* White speech bubble shape */}
              <path d="M28 16C22.48 16 18 20.48 18 26C18 29.5 19.8 32.6 22.5 34.2L20.5 40L26.5 37.5C27.2 37.6 27.9 37.65 28.5 37.65C34.02 37.65 38.5 33.17 38.5 27.65C38.5 22.13 34.02 17.65 28.5 17.65V16Z" fill="white" stroke="#0068FF" strokeWidth="1.5" strokeLinejoin="round" />

              {/* Zalo text inside bubble */}
              <text x="28" y="26" fontFamily="Arial, sans-serif" fontSize="7.5" fontWeight="bold" fill="#0068FF" textAnchor="middle" dominantBaseline="middle">Zalo</text>
            </svg>
          </div>

          {/* Tooltip text */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            {t('home.zaloTooltip')}
            <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
          </div>
        </div>
      </a>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-center mb-16 pt-20 sm:pt-24 relative z-10" id="hero-section">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 sm:mb-12 lg:mb-16 drop-shadow-lg leading-tight px-2">
            <span className="block mb-1 text-red-700 font-extrabold drop-shadow-md">{t('home.hero.titlePrefix')}</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-700 drop-shadow-md mt-1 mb-4 sm:mb-6 lg:mb-8 leading-tight overflow-visible">
              {t('home.hero.titleSuffix')}
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-xl text-gray-900 max-w-3xl mx-auto mb-6 sm:mb-8 drop-shadow-lg px-4 font-semibold">
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
            <div className="flex items-center gap-2 text-gray-700 bg-white px-3 sm:px-4 py-2 rounded-lg shadow-md text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.hero.badgeAutoVideo')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700 bg-white px-3 sm:px-4 py-2 rounded-lg shadow-md text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.hero.badgeMultiLangVoice')}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700 bg-white px-3 sm:px-4 py-2 rounded-lg shadow-md text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.hero.badgeExport4K')}</span>
            </div>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="mb-16">
          {/* Top Row - 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {tools.slice(0, 3).map((tool) => (
              <div
                key={tool.id}
                className="group cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <div className="relative w-full h-full transition-all duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                  {/* Front Face */}
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:shadow-red-500/20 hover:border-red-200 flex flex-col min-h-full [backface-visibility:hidden]">
                    <div className="p-4 sm:p-6 lg:p-8 flex flex-col h-full items-center">
                      {/* Icon */}
                      <div className="flex justify-center items-center mb-4 sm:mb-6 w-full">
                        <div className="text-blue-500 p-3 sm:p-4 bg-blue-50 rounded-2xl shadow-lg">
                          <div className="w-8 h-8 sm:w-12 sm:h-12">{tool.icon}</div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center drop-shadow-sm w-full px-2">
                        {tool.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed text-center w-full px-2">
                        {tool.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 justify-center w-full">
                        {tool.features.map((feature, index) => (
                          <span
                            key={index}
                            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 relative z-10 text-center"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2 mt-auto w-full">
                        <a
                          href="https://www.youtube.com/watch?v=yG30rAl1nvk&t=36s"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-red-500/50 hover:-translate-y-0.5"
                        >
                          {t('home.tools.actions.demo')}
                        </a>
                        {(tool.id === 1 || tool.id === 2 || tool.id === 3) ? (
                          <button
                            onClick={() => setShowPricingModal(true)}
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5"
                          >
                            {t('home.tools.actions.pricing')}
                          </button>
                        ) : (
                          <button className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5">
                            {t('home.tools.actions.pricing')}
                          </button>
                        )}
                        <a
                          href="https://drive.google.com/drive/folders/1NMFWchFhXj_hMpeLbu_0E0V2LWo60PKY?usp=drive_link"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:-translate-y-0.5"
                        >
                          {t('home.tools.actions.download')}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Back Face - Same content */}
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:shadow-red-500/20 hover:border-red-200 flex flex-col h-full [backface-visibility:hidden] [transform:rotateY(180deg)] absolute inset-0">
                    <div className="p-4 sm:p-6 lg:p-8 flex flex-col h-full items-center">
                      {/* Icon */}
                      <div className="flex justify-center items-center mb-4 sm:mb-6 w-full">
                        <div className="text-blue-500 p-3 sm:p-4 bg-blue-50 rounded-2xl shadow-lg">
                          <div className="w-8 h-8 sm:w-12 sm:h-12">{tool.icon}</div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center drop-shadow-sm w-full px-2">
                        {tool.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed text-center w-full px-2">
                        {tool.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 flex-grow justify-center w-full">
                        {tool.features.map((feature, index) => (
                          <span
                            key={`back-${index}`}
                            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 relative z-10 text-center"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2 mt-auto w-full">
                        <a
                          href="https://www.youtube.com/watch?v=yG30rAl1nvk&t=36s"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-red-500/50 hover:-translate-y-0.5"
                        >
                          {t('home.tools.actions.demo')}
                        </a>
                        {(tool.id === 1 || tool.id === 2 || tool.id === 3) ? (
                          <button
                            onClick={() => setShowPricingModal(true)}
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5"
                          >
                            {t('home.tools.actions.pricing')}
                          </button>
                        ) : (
                          <button className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5">
                            {t('home.tools.actions.pricing')}
                          </button>
                        )}
                        <a
                          href="https://drive.google.com/drive/folders/1NMFWchFhXj_hMpeLbu_0E0V2LWo60PKY?usp=drive_link"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:-translate-y-0.5"
                        >
                          {t('home.tools.actions.download')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row - 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {tools.slice(3, 5).map((tool) => (
              <div
                key={tool.id}
                className="group cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <div className="relative w-full h-full transition-all duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                  {/* Front Face */}
                  <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:shadow-red-500/20 hover:border-red-200 flex flex-col min-h-full [backface-visibility:hidden]">
                    <div className="p-4 sm:p-6 lg:p-8 flex flex-col h-full items-center">
                      <div className="flex justify-center items-center mb-4 sm:mb-6 w-full">
                        <div className="text-blue-500 p-3 sm:p-4 bg-blue-50 rounded-2xl shadow-lg">
                          <div className="w-8 h-8 sm:w-12 sm:h-12">{tool.icon}</div>
                        </div>
                      </div>
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center drop-shadow-sm w-full px-2">
                        {tool.name}
                      </h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed text-center w-full px-2">
                        {tool.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 flex-grow justify-center w-full">
                        {tool.features.map((feature, index) => (
                          <span key={index} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 relative z-10 text-center">
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-col gap-2 mt-auto w-full">
                        <a href="https://www.youtube.com/watch?v=yG30rAl1nvk&t=36s" target="_blank" rel="noopener noreferrer" className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-red-500/50 hover:-translate-y-0.5">{t('home.tools.actions.demo')}</a>
                        <button onClick={() => tool.id === 4 ? setShowFullComboModal(true) : null} className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5">{t('home.tools.actions.pricing')}</button>
                        <a href="https://drive.google.com/drive/folders/1NMFWchFhXj_hMpeLbu_0E0V2LWo60PKY?usp=drive_link" target="_blank" rel="noopener noreferrer" className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:-translate-y-0.5">{t('home.tools.actions.download')}</a>
                      </div>
                    </div>
                  </div>
                  {/* Back Face */}
                  <div className="absolute inset-0 bg-white rounded-2xl shadow-xl border-2 border-gray-200 hover:shadow-2xl hover:shadow-red-500/20 hover:border-red-200 flex flex-col [backface-visibility:hidden] [transform:rotateY(180deg)]">
                    <div className="p-4 sm:p-6 lg:p-8 overflow-visible flex flex-col flex-grow items-center">
                      <div className="flex justify-center items-center mb-4 sm:mb-6 w-full">
                        <div className="text-blue-500 p-3 sm:p-4 bg-blue-50 rounded-2xl shadow-lg">
                          <div className="w-8 h-8 sm:w-12 sm:h-12">{tool.icon}</div>
                        </div>
                      </div>
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center drop-shadow-sm w-full px-2">
                        {tool.name}
                      </h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed text-center w-full px-2">
                        {tool.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 flex-grow justify-center w-full">
                        {tool.features.map((feature, index) => (
                          <span key={`back-${index}`} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1 relative z-10 text-center">
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-col gap-2 mt-auto w-full">
                        <a href="https://www.youtube.com/watch?v=yG30rAl1nvk&t=36s" target="_blank" rel="noopener noreferrer" className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-red-500/50 hover:-translate-y-0.5">{t('home.tools.actions.demo')}</a>
                        <button onClick={() => tool.id === 4 ? setShowFullComboModal(true) : null} className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-purple-500/50 hover:-translate-y-0.5">{t('home.tools.actions.pricing')}</button>
                        <a href="https://drive.google.com/drive/folders/1NMFWchFhXj_hMpeLbu_0E0V2LWo60PKY?usp=drive_link" target="_blank" rel="noopener noreferrer" className="w-full inline-block px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs sm:text-sm font-medium text-center transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:-translate-y-0.5">{t('home.tools.actions.download')}</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Modal */}
        {showPricingModal && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowPricingModal(false)}
          >
            <div
              className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  {t('home.pricing.title')}
                </h2>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="text-slate-400 hover:text-white text-2xl font-light w-8 h-8 flex items-center justify-center rounded transition-colors"
                >
                  ×
                </button>
              </div>
              <p className="text-sm sm:text-base text-center text-slate-300 mb-8">
                {t('home.pricing.note')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {/* Gói 1 Tháng */}
                <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-600">
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.pricing.month1')}</h3>
                    <p className="text-sm text-sky-300">{t('home.pricing.rent1')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-400 line-through mb-1">450,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-sky-300">
                      350,000 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.consistentChar')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.unlimitedCreation')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.hdQuality')}</span>
                    </li>
                  </ul>
                </div>

                {/* Gói 3 Tháng */}
                <div className="relative bg-gradient-to-b from-sky-900 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border-2 border-sky-400 scale-[1.05]">
                  <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-sky-400 text-xs font-semibold text-slate-900 shadow-lg">
                    {t('home.pricing.bestValue')}
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.pricing.month3')}</h3>
                    <p className="text-sm text-sky-200">{t('home.pricing.rent3')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-300 line-through mb-1">1,200,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-sky-100">
                      950,000 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.allFeatures')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.studioQuality')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.support247')}</span>
                    </li>
                  </ul>
                </div>

                {/* Gói 5 Tháng */}
                <div className="relative bg-gradient-to-b from-violet-900 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-violet-600">
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.pricing.month5')}</h3>
                    <p className="text-sm text-violet-200">{t('home.pricing.rent5')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-300 line-through mb-1">1,950,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-sky-200">
                      1,550,000 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.unlimited')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.highestQuality')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">✓</span>
                      <span className="text-orange-500">{t('home.pricing.features.support247')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Combo Vip + Voice Modal */}
        {showFullComboModal && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowFullComboModal(false)}
          >
            <div
              className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl p-6 sm:p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  {t('home.fullComboPricing.title')}
                </h2>
                <button
                  onClick={() => setShowFullComboModal(false)}
                  className="text-slate-400 hover:text-white text-2xl font-light w-8 h-8 flex items-center justify-center rounded transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {/* Gói Combo 1 Tháng */}
                <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-600">
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.fullComboPricing.combo1')}</h3>
                    <p className="text-sm text-sky-300">{t('home.fullComboPricing.rent1')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-400 line-through mb-1">950,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-blue-400">
                      686,868 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.items')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.consistentChar')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.unlimited')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.highestQuality')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.free2Acc')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.10threads')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.speedx2')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.optimized')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.proVoice')}</span>
                    </li>
                  </ul>
                </div>

                {/* Gói Combo 3 Tháng */}
                <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-600">
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.fullComboPricing.combo3')}</h3>
                    <p className="text-sm text-sky-300">{t('home.fullComboPricing.rent3')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-400 line-through mb-1">2,500,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-blue-400">
                      1,800,000 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.items')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.consistentChar')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.unlimited')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.highestQuality')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.free2Acc')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.10threads')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.speedx2')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.optimized')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.proVoice')}</span>
                    </li>
                  </ul>
                </div>

                {/* Gói Combo 5 Tháng */}
                <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-600">
                  <div className="text-center mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">{t('home.fullComboPricing.combo5')}</h3>
                    <p className="text-sm text-sky-300">{t('home.fullComboPricing.rent5')}</p>
                  </div>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-sm text-slate-400 line-through mb-1">3,700,000 VNĐ</div>
                    <div className="text-2xl sm:text-3xl font-extrabold text-blue-400">
                      3,000,000 VNĐ
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.items')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.consistentChar')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.unlimited')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.highestQuality')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.free2Acc')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.10threads')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.speedx2')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.optimized')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-400 text-lg">✓</span>
                      <span className="text-white">{t('home.fullComboPricing.features.proVoice')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="max-w-6xl mx-auto mb-12 sm:mb-16 px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8 sm:mb-12 drop-shadow-md">
            {t('home.reasons.title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-4 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('home.reasons.speed.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('home.reasons.speed.desc')}</p>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('home.reasons.quality.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('home.reasons.quality.desc')}</p>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t('home.reasons.price.title')}</h3>
              <p className="text-sm sm:text-base text-gray-600">{t('home.reasons.price.desc')}</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 sm:p-8 lg:p-12 text-white shadow-2xl mx-4 sm:mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 drop-shadow-lg">{t('home.cta.title')}</h2>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 opacity-90 drop-shadow-md px-2">
            {t('home.cta.desc')}
          </p>
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white text-red-600 rounded-xl font-semibold text-base sm:text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
            >
              {t('home.cta.dashboard')}
            </Link>
          ) : (
            <Link
              to="/register"
              className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-white text-red-600 rounded-xl font-semibold text-base sm:text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
            >
              {t('home.cta.register')}
            </Link>
          )}
        </div>
      </div>
    </div >
  );
};

export default HomePage;
